import * as React from 'react';

// --- Lib ---
import { parseShortcutKeys } from '../../../../app/rte/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseDuplicateConfig } from './use-duplicate';
import { DUPLICATE_SHORTCUT_KEY, useDuplicate } from './use-duplicate';

// --- UI Primitives ---
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';

import { Icon, Tooltip } from '@cotera/client/app/components';

export interface DuplicateButtonProps
  extends Omit<React.ComponentProps<'button'>, 'type'>,
    UseDuplicateConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;

  tooltip?: string;
}

export function DuplicateShortcutBadge({
  shortcutKeys = DUPLICATE_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge variant="outline">{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for duplicating a node in a Tiptap editor.
 *
 * For custom button implementations, use the `useDuplicate` hook instead.
 */
export const DuplicateButton = React.forwardRef<
  HTMLButtonElement,
  DuplicateButtonProps
>(
  (
    {
      editor: providedEditor,
      text,
      hideWhenUnavailable = false,
      onDuplicated,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      handleDuplicate,
      label,
      shortcutKeys,
      Icon: iconName,
    } = useDuplicate({
      editor,
      hideWhenUnavailable,
      onDuplicated,
    });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleDuplicate();
      },
      [handleDuplicate, onClick]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Tooltip side="auto" tooltipContent="Duplicate">
        <Button
          type="button"
          variant="ghost"
          role="button"
          tabIndex={-1}
          aria-label={label}
          onClick={handleClick}
          {...buttonProps}
          ref={ref}
        >
          {children ?? (
            <>
              <Icon icon={iconName} className="tiptap-button-icon" />
              {text && <span className="tiptap-button-text">{text}</span>}
              {showShortcut && (
                <DuplicateShortcutBadge shortcutKeys={shortcutKeys} />
              )}
            </>
          )}
        </Button>
      </Tooltip>
    );
  }
);

DuplicateButton.displayName = 'DuplicateButton';
